import type { GameAction, LaneId } from "@fate-kingdom/engine";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";

const TOUCH_UPWARD_THRESHOLD = 12;
const MOUSE_DRAG_THRESHOLD = 7;

export type AlphaAttackAction = Extract<GameAction, { readonly type: "attack" }>;

export interface AlphaFormationDragState {
  readonly active: boolean;
  readonly sourceLaneId: LaneId | null;
  readonly hoveredLaneId: LaneId | null;
  readonly legalTargetLaneIds: readonly LaneId[];
  readonly pointerType: string | null;
  readonly currentX: number;
  readonly currentY: number;
}

const IDLE_STATE: AlphaFormationDragState = {
  active: false,
  sourceLaneId: null,
  hoveredLaneId: null,
  legalTargetLaneIds: [],
  pointerType: null,
  currentX: 0,
  currentY: 0,
};

interface Candidate {
  readonly sourceLaneId: LaneId;
  readonly pointerId: number;
  readonly pointerType: string;
  readonly startX: number;
  readonly startY: number;
  readonly trigger: HTMLButtonElement;
  readonly actionsByTarget: ReadonlyMap<LaneId, AlphaAttackAction>;
  dragging: boolean;
}

function wantsDrag(pointerType: string, dx: number, dy: number): boolean {
  if (pointerType === "mouse") return dy <= -MOUSE_DRAG_THRESHOLD;
  return dy <= -TOUCH_UPWARD_THRESHOLD && Math.abs(dy) > Math.abs(dx);
}

/** Presentation-only Pointer Events adapter for an exact current legal attack set. */
export function useAlphaFormationDrag({
  getLaneAtPoint,
  onDrop,
  onCancel,
}: {
  readonly getLaneAtPoint: (x: number, y: number) => LaneId | null;
  readonly onDrop: (action: AlphaAttackAction) => void;
  readonly onCancel: (message: string) => void;
}) {
  const [state, setState] = useState<AlphaFormationDragState>(IDLE_STATE);
  const candidateRef = useRef<Candidate | null>(null);
  const callbacksRef = useRef({ getLaneAtPoint, onDrop, onCancel });
  const suppressClickRef = useRef<LaneId | null>(null);

  useEffect(() => {
    callbacksRef.current = { getLaneAtPoint, onDrop, onCancel };
  }, [getLaneAtPoint, onCancel, onDrop]);

  const clear = useCallback(() => {
    const candidate = candidateRef.current;
    if (candidate?.trigger.hasPointerCapture(candidate.pointerId)) {
      candidate.trigger.releasePointerCapture(candidate.pointerId);
    }
    candidateRef.current = null;
    setState(IDLE_STATE);
  }, []);

  const cancel = useCallback(
    (message = "Attack cancelled.") => {
      if (candidateRef.current?.dragging)
        suppressClickRef.current = candidateRef.current.sourceLaneId;
      if (candidateRef.current !== null || state.active) callbacksRef.current.onCancel(message);
      clear();
    },
    [clear, state.active],
  );

  useEffect(() => {
    const move = (event: PointerEvent) => {
      const candidate = candidateRef.current;
      if (candidate === null || candidate.pointerId !== event.pointerId) return;
      const dx = event.clientX - candidate.startX;
      const dy = event.clientY - candidate.startY;
      if (!candidate.dragging) {
        if (!wantsDrag(candidate.pointerType, dx, dy)) return;
        candidate.dragging = true;
        if (candidate.pointerType !== "mouse")
          candidate.trigger.setPointerCapture(candidate.pointerId);
      }
      event.preventDefault();
      setState({
        active: true,
        sourceLaneId: candidate.sourceLaneId,
        hoveredLaneId: callbacksRef.current.getLaneAtPoint(event.clientX, event.clientY),
        legalTargetLaneIds: [...candidate.actionsByTarget.keys()],
        pointerType: candidate.pointerType,
        currentX: event.clientX,
        currentY: event.clientY,
      });
    };
    const up = (event: PointerEvent) => {
      const candidate = candidateRef.current;
      if (candidate === null || candidate.pointerId !== event.pointerId) return;
      if (!candidate.dragging) return clear();
      suppressClickRef.current = candidate.sourceLaneId;
      const targetLaneId = callbacksRef.current.getLaneAtPoint(event.clientX, event.clientY);
      const action =
        targetLaneId === null ? undefined : candidate.actionsByTarget.get(targetLaneId);
      clear();
      if (action === undefined)
        callbacksRef.current.onCancel("That Land is not a legal attack target.");
      else callbacksRef.current.onDrop(action);
    };
    const cancelled = () => cancel();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") cancel();
    };
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", up, { passive: false });
    window.addEventListener("pointercancel", cancelled);
    window.addEventListener("blur", cancelled);
    window.addEventListener("keydown", keydown);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancelled);
      window.removeEventListener("blur", cancelled);
      window.removeEventListener("keydown", keydown);
    };
  }, [cancel, clear]);

  const beginPress = useCallback(
    (
      sourceLaneId: LaneId,
      actionsByTarget: ReadonlyMap<LaneId, AlphaAttackAction>,
      event: ReactPointerEvent<HTMLButtonElement>,
    ) => {
      if (event.button !== 0 || actionsByTarget.size === 0) return;
      candidateRef.current = {
        sourceLaneId,
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        startX: event.clientX,
        startY: event.clientY,
        trigger: event.currentTarget,
        actionsByTarget,
        dragging: false,
      };
    },
    [],
  );

  const consumeSuppressedClick = useCallback((laneId: LaneId) => {
    if (suppressClickRef.current !== laneId) return false;
    suppressClickRef.current = null;
    return true;
  }, []);

  return { state, beginPress, cancel, consumeSuppressedClick };
}
